import {
  DoseDispensingProductAvailability,
  DoseDispensingProductHalving,
  DoseDispensingProductType,
  MultilingualKeyDto,
} from '@pharmac-extranet/extranet-api'
import { ControlledCheckboxes, ControlledInput, useLanguage } from '@pharmac-extranet/shared'
import { PharmacButton } from '@pharmac/ui'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import {
  ExtranetFiltersColumn,
  ExtranetFiltersFooter,
  ExtranetFiltersRow,
} from '../../components/filters/ExtranetFiltersComponents'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { GetDoseDispensingProductsParams } from '../../views/dose-dispensing-catalog/DoseDispensingCatalogView'
import { mapSearchParamsToQueryParams } from './util/query-parameter-mapping'

type Props = {
  closeFilters: () => void
}

export const DoseDispensingCatalogFilters = ({ closeFilters }: Props) => {
  const { t } = useLanguage(['extranet'])

  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(mapSearchParamsToQueryParams(searchParams))

  const formHook = useForm<GetDoseDispensingProductsParams>({
    mode: 'onChange',
    defaultValues: {
      manufacturerName: filters.manufacturerName ?? '',
      halving: filters.halving ?? [],
      availableForCustomers: filters.availableForCustomers ?? [],
      type: filters.type ?? [],
    },
  })

  useEffect(() => {
    const params = mapSearchParamsToQueryParams(searchParams)

    setFilters(params)

    formHook.reset({
      manufacturerName: params.manufacturerName ?? '',
      halving: params.halving ?? [],
      availableForCustomers: params.availableForCustomers ?? [],
      type: params.type ?? [],
    })
  }, [searchParams])

  const availableForCustomers: Array<{
    value: DoseDispensingProductAvailability
    name: MultilingualKeyDto
  }> = [
    {
      value: DoseDispensingProductAvailability.Available,
      name: {
        fi: t('medicines.filters.Available'),
        sv: t('medicines.filters.Available'),
        en: t('medicines.filters.Available'),
      },
    },
    {
      value: DoseDispensingProductAvailability.InShortSupply,
      name: {
        fi: t('medicines.filters.InShortSupply'),
        en: t('medicines.filters.InShortSupply'),
        sv: t('medicines.filters.InShortSupply'),
      },
    },
    {
      value: DoseDispensingProductAvailability.OutOfStock,
      name: {
        fi: t('medicines.filters.OutOfStock'),
        en: t('medicines.filters.OutOfStock'),
        sv: t('medicines.filters.OutOfStock'),
      },
    },
    {
      value: DoseDispensingProductAvailability.ToBeDiscontinued,
      name: {
        fi: t('medicines.filters.ToBeDiscontinued'),
        en: t('medicines.filters.ToBeDiscontinued'),
        sv: t('medicines.filters.ToBeDiscontinued'),
      },
    },
    {
      value: DoseDispensingProductAvailability.Discontinued,
      name: {
        fi: t('medicines.filters.Discontinued'),
        en: t('medicines.filters.Discontinued'),
        sv: t('medicines.filters.Discontinued'),
      },
    },
  ]

  const halving: Array<{
    value: DoseDispensingProductHalving
    name: MultilingualKeyDto
  }> = [
    {
      value: DoseDispensingProductHalving.Yes,
      name: {
        fi: t('medicines.filters.halvingYes'),
        en: t('medicines.filters.halvingYes'),
        sv: t('medicines.filters.halvingYes'),
      },
    },
    {
      value: DoseDispensingProductHalving.No,
      name: {
        fi: t('medicines.filters.halvingNo'),
        en: t('medicines.filters.halvingNo'),
        sv: t('medicines.filters.halvingNo'),
      },
    },
    {
      value: DoseDispensingProductHalving.Always,
      name: {
        fi: t('medicines.filters.halvingAlways'),
        en: t('medicines.filters.halvingAlways'),
        sv: t('medicines.filters.halvingAlways'),
      },
    },
  ]

  const type: Array<{
    value: DoseDispensingProductType
    name: MultilingualKeyDto
  }> = [
    {
      value: DoseDispensingProductType.Drug,
      name: {
        fi: t('medicines.filters.drug'),
        en: t('medicines.filters.drug'),
        sv: t('medicines.filters.drug'),
      },
    },
    {
      value: DoseDispensingProductType.Supplement,
      name: {
        fi: t('medicines.filters.supplement'),
        en: t('medicines.filters.supplement'),
        sv: t('medicines.filters.supplement'),
      },
    },
  ]

  const handleSubmit = (values: GetDoseDispensingProductsParams) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...filters,
          ...values,
        })
      )
    )

    closeFilters()
  }

  return (
    <>
      <FormProvider {...formHook}>
        <ExtranetFiltersRow>
          <ExtranetFiltersColumn>
            <ControlledCheckboxes
              column
              data={availableForCustomers}
              name="availableForCustomers"
              control={formHook.control}
              label={t('medicines.availableForCustomers')}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledCheckboxes
              column
              data={halving}
              name="halving"
              control={formHook.control}
              label={t('medicines.halvable')}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledInput
              label={t('medicines.manufacturerName')}
              name="manufacturerName"
              control={formHook.control}
              placeholder={t('medicines.searchManufacturer')}
            />
          </ExtranetFiltersColumn>
          <ExtranetFiltersColumn>
            <ControlledCheckboxes
              column
              data={type}
              name="type"
              control={formHook.control}
              label={t('medicines.medicineType')}
            />
          </ExtranetFiltersColumn>
        </ExtranetFiltersRow>
        <ExtranetFiltersFooter>
          <PharmacButton
            onClick={formHook.handleSubmit(handleSubmit, (a) => console.log(a))}
            disabled={!formHook.formState.isDirty}
          >
            {t('search', { ns: 'common' })}
          </PharmacButton>
        </ExtranetFiltersFooter>
      </FormProvider>
    </>
  )
}
